import * as _ from 'lodash';
import * as servicesApi from '../commons/testimServicesApi';
import { getLogger } from '../commons/logger';
import { guid, hasTestPlanFlag, promiseMap } from '../utils';
import { gridMessages, gridTypes } from '../commons/constants';
import { GridError, ArgError, GridConcurrencyError } from '../errors';
import type { GridType } from '../commons/constants';
import type { AwaitedReturnType } from '../utils';
import type { RunnerOptions } from '../runOptions';
import type { CommonSharedOptions } from '../runner';
import type { TestRunHandler } from '../testRunHandler';
import type { LambdatestService } from './lambdatestService';
import type { RawTestPlan } from 'services/src/commons/mongo/models/testPlan';
import type { RawGrid, TunnelType } from 'services/src/commons/mongo/models/grid';

const logger = getLogger('grid-service');

type VendorType = 'testobject' | 'saucelabs' | 'perfecto' | 'hostAndPort';
const gridCache: Record<number, { gridId: string; companyId: string; slotId: string; browser: string; isSupportGrid?: boolean }> = {};
const urlExtractRegex = /(^(https?):\/{2})?(.*)/;
let keepAliveTimer: NodeJS.Timeout | null = null;

function extractProtocol(grid: GridSerializationInput) {
    if (grid.protocol) {
        return grid.protocol;
    }

    if ([gridTypes.TESTIM_PRIVATE, gridTypes.TESTIM, gridTypes.BROWSERSTACK, gridTypes.SAUCELABS].includes(grid.type as any) && grid.port === 443) {
        return 'https';
    }

    if ([gridTypes.TESTIM_ENTERPRISE, gridTypes.TESTIM_LAMBDATEST, gridTypes.DEVICE_FARM].includes(grid.type as any)) {
        const urlExtract = urlExtractRegex.exec(grid.host!);
        return (urlExtract?.[2] as 'http') || 'https';
    }

    return '';
}

function extractHost(hostUrl?: string) {
    const urlExtract = urlExtractRegex.exec(hostUrl!);
    return urlExtract?.[3];
}

type HybridGridProvider = AwaitedReturnType<typeof servicesApi.getHybridGridProvider>;
export type GridSerializationInput = RawGrid & Pick<HybridGridProvider['connectionDetails'], 'external'> & SerializedGrid;
export interface SerializedGrid {
    mode?: string;
    host?: string;
    port?: number;
    path?: string;
    protocol?: string;
    accessToken?: string;
    slotId?: string;
    gridId?: string;
    tunnel?: TunnelType;
    user?: string;
    key?: string;
    type?: GridType;
    name?: string;
    provider?: string;
    tunnelUser?: string;
    tunnelKey?: string;
    androidToken?: string;
    iosToken?: string;
    apiUrl?: string;
    isSupportGrid?: boolean;
}
function getSerializableObject(grid: GridSerializationInput): SerializedGrid {
    const host = grid && extractHost(grid.host);
    const port = grid.port;
    const path = grid.path;
    const protocol = grid && extractProtocol(grid);
    const accessToken = grid.token;
    const slotId = grid.slotId;
    const tunnel = grid.hybrid?.tunnel;
    const user = grid.external?.user;
    const key = grid.external?.key;
    const type = grid.type;
    const tunnelUser = type === gridTypes.HYBRID ? (tunnel && grid.hybrid?.external?.[grid.hybrid.tunnel as keyof typeof grid.hybrid.external]?.user) : user;
    const tunnelKey = type === gridTypes.HYBRID ? (tunnel && grid.hybrid?.external?.[grid.hybrid.tunnel as keyof typeof grid.hybrid.external]?.key) : key;
    const name = grid.name;
    const gridId = grid && (grid._id || grid.gridId);
    const provider = grid.provider;
    const androidToken = grid.mobileTokens?.androidToken;
    const iosToken = grid.mobileTokens?.iosToken;
    const apiUrl = grid.apiUrl;
    const isSupportGrid = grid.isSupportGrid;

    return {
        host, port, path, protocol, accessToken, slotId, gridId, tunnel, user, key, type, name, provider, tunnelUser, tunnelKey, androidToken, iosToken, apiUrl, isSupportGrid,
    };
}

export const addItemToGridCache = (workerId: number, companyId: string, gridId: string, slotId: string, browser: string, isSupportGrid?: boolean) => {
    gridCache[workerId] = { gridId, companyId, slotId, browser, isSupportGrid };
};

type GridRes = { status: 'success'; grid: GridSerializationInput } | { status: 'error'; code: string };
async function handleGetGridResponse(projectId: string, companyId: string, workerId: number, browser: string, options: RunnerOptions, getFun: () => Promise<GridRes>) {
    let res: Awaited<ReturnType<typeof getFun>>;
    try {
        res = await getFun();
    } catch (err) {
        logger.error('failed to get grid', { projectId, companyId, err });
        throw new Error(gridMessages.UNKNOWN);
    }
    logger.info('get grid info', { ...res, projectId, companyId });
    if (!res || (!['success', 'error'].includes(res.status))) {
        logger.error('invalid response - get grid', { res });
        throw new Error(gridMessages.UNKNOWN);
    }

    if (res.status === 'success') {
        if (res.grid.mode === 'local') {
            return { mode: 'local' };
        }
        if (res.grid?.mode === 'custom') {
            Object.assign(res.grid, getOptionGrid(options));
        }
        const serGrid = getSerializableObject(res.grid);
        addItemToGridCache(workerId, companyId, serGrid.gridId!, serGrid.slotId!, browser, serGrid.isSupportGrid);
        return serGrid;
    }

    if (res.code === 'not-found') {
        throw new GridError(gridMessages.NOT_FOUND);
    }

    if (res.code === 'no-available-slot') {
        throw new GridConcurrencyError(`Failed to run test on ${browser} - concurrency limit reached`);
    }

    logger.error('invalid code error response - get grid', { res });
    throw new GridError(gridMessages.UNKNOWN);
}

function getAllGrids(companyId: string) {
    return servicesApi.getAllGrids(companyId);
}

async function getGridDataByGridId(companyId: string, gridId: string, allGrids?: RawGrid[]) {
    const grids = allGrids || await getAllGrids(companyId);
    const grid = grids.find(g => g._id === gridId);
    if (!grid) {
        throw new ArgError(`Failed to find grid id: ${gridId}`);
    }
    return getSerializableObject(grid as GridSerializationInput);
}

async function getGridDataByGridName(companyId: string, gridName: string, allGrids?: RawGrid[]) {
    const grids = allGrids || await getAllGrids(companyId);
    const grid = grids.find(g => (g.name || '').toLowerCase() === gridName.toLowerCase());
    if (!grid) {
        throw new ArgError(`Failed to find grid name: ${gridName}`);
    }
    return getSerializableObject(grid as GridSerializationInput);
}

export async function releaseGridSlot(workerId: number, projectId: string) {
    const gridData = gridCache[workerId];
    if (!gridData) {
        return;
    }

    const { slotId, gridId, browser, companyId, isSupportGrid } = gridData;
    delete gridCache[workerId];

    if (isSupportGrid) {
        return;
    }

    if (!slotId) {
        logger.warn('failed to find grid slot id', { projectId });
        return;
    }

    logger.info('release slot id', { projectId, companyId, slotId, gridId, browser });
    try {
        await servicesApi.releaseGridSlot(companyId, projectId, slotId, gridId, browser);
    } catch (err) {
        logger.error('failed to release slot', { projectId, err });
    }
}

async function _keepAlive(projectId: string) {
    const slots = Object.values(gridCache).filter(Boolean);

    if (slots.length === 0) {
        return;
    }

    if (slots[0].isSupportGrid) {
        return;
    }

    try {
        await servicesApi.keepAliveGrid(projectId, slots);
    } catch (err) {
        logger.error('failed to update grid keep alive', { err, slots, projectId });
    }
}

function startKeepAlive(projectId: string) {
    const KEEP_ALIVE_INTERVAL = 10 * 1000;
    keepAliveTimer = global.setInterval(_keepAlive, KEEP_ALIVE_INTERVAL, projectId);
}

async function releaseAllSlots(projectId: string) {
    const workerIds = Object.keys(gridCache);

    if (!workerIds.length) {
        return;
    }

    logger.warn('not all slots released before end runner flow', { projectId });
    try {
        await promiseMap(workerIds, workerId => releaseGridSlot(Number(workerId), projectId));
    } catch (err) {
        logger.error('failed to release all slots', { err, projectId });
    }
}

async function endKeepAlive(projectId: string) {
    await releaseAllSlots(projectId);
    clearInterval(keepAliveTimer!);
}
export const keepAlive = {
    start: startKeepAlive,
    end: endKeepAlive,
};

function getVendorKeyFromOptions(type: VendorType, options: CommonSharedOptions) {
    const { testobjectSauce, saucelabs } = options;
    if (type === 'testobject') {
        return testobjectSauce!.testobjectApiKey;
    }
    if (type === 'saucelabs') {
        return saucelabs!.accessKey;
    }
    return undefined;
}

function getVendorUserFromOptions(type: VendorType, options: CommonSharedOptions) {
    const { saucelabs } = options;
    if (type === 'saucelabs') {
        return saucelabs!.username;
    }
    return undefined;
}

function getOptionGrid(options: CommonSharedOptions): SerializedGrid {
    const getGridType = () => {
        if (!_.isEmpty(options.testobjectSauce)) {
            return ('testobject' as any);
        }

        if (!_.isEmpty(options.saucelabs)) {
            return 'saucelabs';
        }

        if (!_.isEmpty(options.perfecto)) {
            return 'perfecto';
        }

        return 'hostAndPort';
    };
    const type = getGridType();
    const { host, port, path, protocol } = options;
    const key = getVendorKeyFromOptions(type, options);
    const user = getVendorUserFromOptions(type, options);
    return { host, port, path, protocol, type, user, key, gridId: guid() };
}

export async function getTestPlanGridData(options: RunnerOptions, testPlanData: RawTestPlan) {
    const companyId = options.company!.companyId;
    return await getGridDataByGridId(companyId, testPlanData.gridId, options.allGrids);
}

export async function getGridData(options: CommonSharedOptions): Promise<SerializedGrid | undefined> {
    const { allGrids, company, host, useLocalChromeDriver, localTmaUrl, useChromeLauncher, gridId, grid } = options;
    if (useLocalChromeDriver || useChromeLauncher || localTmaUrl) {
        return { mode: 'local' };
    }
    if (host) {
        return getOptionGrid(options);
    }
    const companyId = company?.companyId;
    if (gridId) {
        return getGridDataByGridId(companyId!, gridId, allGrids);
    }
    if (grid) {
        return getGridDataByGridName(companyId!, grid, allGrids);
    }
    if (hasTestPlanFlag(options) || options.tunnelOnlyMode) {
        logger.info('skipping getting grid, as it is set on test plan', { companyId });
        return undefined;
    }

    throw new GridError('Missing host or grid configuration');
}

export async function getGridSlot(browser: string, executionId: string, options: RunnerOptions, workerId: number, testResultId: string): Promise<SerializedGrid | { mode: string }> {
    const { host, project: projectId, grid: gridName, gridId, localTmaUrl, useLocalChromeDriver, useChromeLauncher, company } = options;
    const isRunningLocalMode = Boolean(useLocalChromeDriver || useChromeLauncher || localTmaUrl);

    const companyId = company?.companyId;
    const getSlot = () => servicesApi.getGridSlot({ projectId, companyId, browser, executionId, isRunningLocalMode, gridId, gridName, host, testResultId });
    return handleGetGridResponse(projectId, companyId!, workerId, browser, options, getSlot);
}

export const handleHybridOrVendorIfNeeded = async (
    runnerOptions: Partial<RunnerOptions> = { },
    gridInfo: Partial<SerializedGrid> = {},
    testRunConfig: Partial<TestRunHandler['runConfig']> = {},
    lambdatestService: Partial<LambdatestService> = {},
    retryConfig: { maxRetries?: number; currentRetry?: number } = {},
) => {
    const { company: { companyId } = {}, project: projectId } = runnerOptions;
    const { gridId, type } = gridInfo as SerializedGrid;
    const browser = runnerOptions.browser || testRunConfig.browserValue;
    const usingTunnel = Boolean(runnerOptions.tunnel);
    const { maxRetries, currentRetry } = retryConfig;

    if (!gridId || !type) {
        return gridInfo;
    }

    if (type === gridTypes.TESTIM_LAMBDATEST || type === gridTypes.LAMBDATEST) {
        await lambdatestService.enableIfNeeded?.(gridInfo, companyId!, projectId);
    }

    if (type !== gridTypes.HYBRID || !companyId || !browser || !maxRetries || !currentRetry) {
        return gridInfo;
    }

    const response = await servicesApi.getHybridGridProvider({ companyId, gridId, maxRetries, currentRetry, browser, usingTunnel });
    logger.info('handling hybrid grid', { response, companyId });
    const gridData = getSerializableObject({ ...gridInfo, ...response.connectionDetails, provider: response.provider } as GridSerializationInput);
    if (response.provider !== 'lambdatest') {
        lambdatestService.disable?.();
    }
    if (response.provider === 'lambdatest') {
        await lambdatestService.enableIfNeeded?.(gridData, companyId, projectId);
    }

    return gridData;
};
